/** IQ item category for non-stock / reporting-only lines (stock field 5). */
export const STOCK_TAKE_NON_STOCK_CATEGORY = 2;

export interface IStockTakeReportMapping {
  factor: number;
  reportItemCode: string;
}

/**
 * Whether stock field 5 marks the item as a non-stock item that IQ will not accept on a stock take.
 */
export function isStockTakeNonStockCategory(category: string | number | null | undefined): boolean {
  if (category === null || category === undefined || category === '') {
    return false;
  }
  return Number(category) === STOCK_TAKE_NON_STOCK_CATEGORY;
}

/**
 * Normalises stock field 37 (report-to item code). Blank means the item does not report to another item.
 */
export function normalizeStockTakeReportItemCode(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
}

/**
 * Stock field 38 (report factor). Invalid or non-positive values count as 1.
 */
export function resolveStockTakeReportFactor(value: string | number | null | undefined): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 1;
  }
  return parsed;
}

/**
 * Stock field 45 (noDecimal / disallow decimals).
 * TRUE means whole numbers only; FALSE (or missing) means decimals are allowed.
 */
export function resolveStockTakeNoDecimal(value: unknown): boolean {
  if (value === true || value === 1 || value === -1) {
    return true;
  }
  if (typeof value === 'string') {
    const normalised = value.trim().toLowerCase();
    if (
      normalised === 'true' ||
      normalised === 't' ||
      normalised === 'yes' ||
      normalised === 'y' ||
      normalised === '1' ||
      normalised === '-1'
    ) {
      return true;
    }
  }
  return false;
}

/**
 * When field 37 is set, counts must be applied to that parent item using field 38 as the multiplier.
 */
export function resolveStockTakeReportMapping(
  reportItemCode: string | number | null | undefined,
  reportItemFactor: string | number | null | undefined,
  scannedCode: string,
): IStockTakeReportMapping | null {
  const parentCode = normalizeStockTakeReportItemCode(reportItemCode);
  if (!parentCode || parentCode === scannedCode.trim()) {
    return null;
  }
  return {
    reportItemCode: parentCode,
    factor: resolveStockTakeReportFactor(reportItemFactor),
  };
}

/**
 * True when stock field 37 is set to another item, so this item cannot be counted itself.
 */
export function hasStockTakeReportItemCode(value: string | number | null | undefined): boolean {
  return normalizeStockTakeReportItemCode(value).length > 0;
}

/**
 * Whether a counted line should be omitted from IQ submission.
 */
export function shouldExcludeStockTakeItemFromIqSubmission(item: {
  category?: string | number | null;
  reportItemCode?: string | number | null;
}): boolean {
  if (isStockTakeNonStockCategory(item.category)) {
    return true;
  }
  return hasStockTakeReportItemCode(item.reportItemCode);
}
